import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';

const EASE_REFINED = [0.22, 1, 0.36, 1];

const testimonials = [
  {
    quote: 'Stavyn flagged 12 of our high-performers as at risk. We acted on it and kept every single one. The ROI was obvious.',
    name: 'Zeraya Chen',
    title: 'Co-Founder, Xelatek',
    initials: 'ZC',
    stat: '$2.1M',
    statLabel: 'saved in replacement costs',
  },
  {
    quote: 'We were bleeding project managers and superintendents to the competition. Stavyn showed us who was at risk before they had one foot out the door. We got ahead of it and kept our best people on our biggest jobs.',
    name: 'Marcus Johnson',
    title: 'Founder, GBC',
    initials: 'MJ',
    stat: '88%',
    statLabel: 'fewer surprise resignations',
  },
  {
    quote: 'We\'d been losing at least one person a month since 2023. It was just the norm. With Stavyn we could see who was at risk and fix comp and scheduling before they left. For the first time, we didn\'t lose anyone.',
    name: 'Emily Rodriguez',
    title: 'CEO, A+ Dental',
    initials: 'ER',
    stat: '0',
    statLabel: 'employees lost last month',
  },
];

export default function Testimonials({ embedded = false }) {
  const [current, setCurrent] = useState(0);
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: '-80px' });

  useEffect(() => {
    if (!inView) return;
    const interval = setInterval(() => {
      setCurrent(prev => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [inView]);

  const t = testimonials[current];

  return (
    <motion.section
      ref={sectionRef}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: EASE_REFINED }}
      style={{
        background: embedded ? 'transparent' : 'var(--bg-s1)',
        padding: '120px 0',
        position: 'relative',
        overflow: embedded ? 'visible' : 'hidden',
        borderTop: embedded ? 'none' : '1px solid var(--border-divider)',
      }}
    >
      <div className="max-w-7xl mx-auto px-8 lg:px-12 relative z-[1]">

        {/* Header */}
        <div className="mb-16 flex flex-col lg:flex-row lg:items-end lg:justify-between">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, ease: EASE_REFINED }}
          >
            <div className={`section-tag mb-8${embedded ? ' stories-faq-bridge__tag' : ''}`}>Success Stories</div>
            <h2
              className="landing-heading"
              style={{
              fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
              lineHeight: 1.1,
              color: 'var(--text-primary)',
              maxWidth: 440,
            }}
            >
              Results that speak{' '}
              <span className="headline-accent">for themselves.</span>
            </h2>
          </motion.div>

          {/* Navigation dots */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, ease: EASE_REFINED, delay: 0.2 }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 4 }}
          >
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                style={{
                  width: current === i ? 22 : 6,
                  height: 6,
                  borderRadius: 3,
                  background: current === i ? (embedded ? 'var(--sfb-accent)' : 'var(--accent-blue)') : 'var(--border-main)',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'all 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
                }}
              />
            ))}
          </motion.div>
        </div>

        {/* Testimonial card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={current}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: EASE_REFINED }}
          >
            <div
              className={`grid lg:grid-cols-3 gap-0${embedded ? ' stories-faq-bridge__testimonial-card' : ''}`}
              style={{
              border: embedded ? '1px solid var(--sfb-card-border)' : '1px solid var(--border-main)',
              borderRadius: 8,
              overflow: 'hidden',
              background: 'var(--bg-card)',
              boxShadow: embedded ? 'var(--sfb-card-shadow)' : '0 2px 12px var(--shadow-card)',
            }}>

              {/* Left — portrait & meta */}
              <div
                className={`${embedded ? 'stories-faq-bridge__testimonial-meta' : ''} border-b lg:border-b-0 lg:border-r`}
                style={{
                background: embedded ? 'var(--sfb-card-side)' : 'var(--bg-s1)',
                padding: '2rem 1.5rem',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                borderColor: embedded ? 'var(--sfb-card-border)' : 'var(--border-main)',
              }}>
                <div>
                  {/* Avatar */}
                  <div style={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    background: embedded ? 'var(--sfb-accent-soft)' : 'var(--bg-card)',
                    border: embedded ? '1px solid var(--sfb-tag-border)' : '1px solid var(--border-main)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1.25rem',
                  }}>
                    <span style={{
                      fontFamily: 'Inter, DM Sans, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: embedded ? 'var(--sfb-accent)' : 'var(--text-secondary)',
                      letterSpacing: '0.04em',
                    }}>
                      {t.initials}
                    </span>
                  </div>

                  <div style={{
                    fontFamily: 'DM Sans, Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '0.9375rem',
                    color: 'var(--text-primary)',
                    marginBottom: '0.3125rem',
                  }}>
                    {t.name}
                  </div>
                  <div style={{
                    fontFamily: 'DM Sans, Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: '0.8125rem',
                    color: 'var(--text-muted)',
                    letterSpacing: '0.01em',
                  }}>
                    {t.title}
                  </div>
                </div>

                {/* Stat */}
                <div className="mt-8 lg:mt-10">
                  <div style={{ height: 1, background: 'var(--border-divider)', marginBottom: '1.25rem' }}/>
                  <div style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: '2.75rem',
                    lineHeight: 1,
                    color: embedded ? 'var(--sfb-stat-fg)' : 'var(--text-primary)',
                    marginBottom: '0.4375rem',
                    letterSpacing: '-0.03em',
                  }}>
                    {t.stat}
                  </div>
                  <div style={{
                    fontFamily: 'DM Sans, Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: '0.75rem',
                    color: 'var(--text-muted)',
                    letterSpacing: '0.02em',
                  }}>
                    {t.statLabel}
                  </div>
                </div>
              </div>

              {/* Right — quote (spans 2 cols) */}
              <div
                className="lg:col-span-2 px-6 py-8 md:px-10 md:py-12 flex flex-col justify-center relative"
              >
                {/* Large decorative quote mark — desktop only */}
                <div
                  aria-hidden="true"
                  className="hidden md:block absolute top-6 left-10 select-none pointer-events-none"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '7rem',
                    lineHeight: 1,
                    color: embedded ? 'var(--sfb-accent)' : 'var(--accent-blue)',
                    opacity: embedded ? 0.12 : 0.06,
                    fontWeight: 700,
                  }}
                >
                  &ldquo;
                </div>

                <blockquote
                  className="testimonial-quote relative z-[1] mb-8 md:mb-10"
                  style={{
                    fontFamily: 'DM Sans, Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: 'clamp(1rem, 1.75vw, 1.3125rem)',
                    lineHeight: 1.6,
                    color: 'var(--text-primary)',
                    letterSpacing: '-0.005em',
                  }}
                >
                  {t.quote}
                </blockquote>

                {/* Thumbnail strip */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {testimonials.map((test, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrent(i)}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        background: 'var(--bg-s1)',
                        border: `1.5px solid ${i === current ? (embedded ? 'var(--sfb-accent)' : 'var(--accent-blue)') : 'var(--border-main)'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        padding: 0,
                        opacity: i === current ? 1 : 0.5,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <span style={{ fontFamily: 'Inter, DM Sans, sans-serif', fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600 }}>
                        {test.initials}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.section>
  );
}
